import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '@components/Button';
import {
  fetchBlogPostById,
  updateBlogPost,
  type BlogPostChanges,
} from '@lib/api';

type BlogFormState = {
  title: string;
  slug: string;
  excerpt: string;
  cover_image: string;
  content: string;
  is_published: boolean;
};

const blankForm: BlogFormState = {
  title: '',
  slug: '',
  excerpt: '',
  cover_image: '',
  content: '',
  is_published: false,
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

function AdminBlogEditPost() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState<BlogFormState>({ ...blankForm });
  const [formError, setFormError] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadPost = async () => {
      if (!postId) {
        setLoadError('Missing blog post ID.');
        setLoading(false);
        return;
      }
      setLoadError(null);
      setLoading(true);
      try {
        const post = await fetchBlogPostById(postId);
        if (!active) return;
        setFormValues({
          title: post.title,
          slug: post.slug,
          excerpt: post.excerpt ?? '',
          cover_image: post.cover_image ?? '',
          content: post.content,
          is_published: post.is_published,
        });
      } catch (err) {
        if (!active) return;
        setLoadError(
          err instanceof Error
            ? err.message
            : 'Unable to load that blog post right now.'
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadPost();
    return () => {
      active = false;
    };
  }, [postId]);

  const handleFormChange = (
    field: keyof BlogFormState,
    value: string | boolean
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const submitForm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    if (!postId) {
      setFormError('Missing blog post ID.');
      return;
    }

    const title = formValues.title.trim();
    const normalizedSlug = slugify(formValues.slug || formValues.title);
    const content = formValues.content.trim();

    if (!title) {
      setFormError('Title is required.');
      return;
    }
    if (!normalizedSlug) {
      setFormError('Slug is required.');
      return;
    }
    if (!content) {
      setFormError('Content is required.');
      return;
    }

    setFormSubmitting(true);

    try {
      const payload: BlogPostChanges = {
        title,
        slug: normalizedSlug,
        excerpt: formValues.excerpt.trim() || null,
        cover_image: formValues.cover_image.trim() || null,
        content,
        is_published: formValues.is_published,
        published_at: formValues.is_published ? new Date().toISOString() : null,
      };
      await updateBlogPost(postId, payload);
      // regenerate sitemap if post is published after update
      if (payload.is_published) {
        try {
          void fetch('/api/regenerate-sitemap', { method: 'POST' });
        } catch {
          // ignore — best effort
        }
      }
      navigate('/admin/blog');
    } catch (err) {
      setFormError(
        err instanceof Error
          ? err.message
          : 'Unable to update the blog post right now.'
      );
    } finally {
      setFormSubmitting(false);
    }
  };

  const renderForm = () => (
    <form onSubmit={submitForm} className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label
            htmlFor="blog-title"
            className="mb-1 block text-sm font-semibold text-gray-700"
          >
            Title
          </label>
          <input
            id="blog-title"
            type="text"
            value={formValues.title}
            onChange={(e) => handleFormChange('title', e.target.value)}
            className="w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          />
        </div>
        <div>
          <label
            htmlFor="blog-slug"
            className="mb-1 block text-sm font-semibold text-gray-700"
          >
            Slug
          </label>
          <input
            id="blog-slug"
            type="text"
            value={formValues.slug}
            onChange={(e) => handleFormChange('slug', slugify(e.target.value))}
            placeholder="friendly-post-title"
            className="w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Used for the public URL. Only lowercase letters, numbers, and
            hyphens.
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label
            htmlFor="blog-cover"
            className="mb-1 block text-sm font-semibold text-gray-700"
          >
            Cover image URL
          </label>
          <input
            id="blog-cover"
            type="url"
            value={formValues.cover_image}
            onChange={(e) => handleFormChange('cover_image', e.target.value)}
            placeholder="https://example.com/cover.jpg"
            className="w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-gray-200 px-4 py-3">
          <input
            id="blog-active"
            type="checkbox"
            checked={formValues.is_published}
            onChange={(e) => handleFormChange('is_published', e.target.checked)}
            className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <div>
            <label
              htmlFor="blog-active"
              className="text-sm font-semibold text-gray-800"
            >
              Active / Published
            </label>
            <p className="text-xs text-gray-500">
              Toggling this will immediately publish or hide the post.
            </p>
          </div>
        </div>
      </div>

      <div>
        <label
          htmlFor="blog-excerpt"
          className="mb-1 block text-sm font-semibold text-gray-700"
        >
          Excerpt
        </label>
        <textarea
          id="blog-excerpt"
          value={formValues.excerpt}
          onChange={(e) => handleFormChange('excerpt', e.target.value)}
          rows={3}
          className="w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="Short summary used in previews."
        />
      </div>

      <div>
        <label
          htmlFor="blog-content"
          className="mb-1 block text-sm font-semibold text-gray-700"
        >
          Content (Markdown)
        </label>
        <textarea
          id="blog-content"
          value={formValues.content}
          onChange={(e) => handleFormChange('content', e.target.value)}
          rows={12}
          className="w-full rounded-2xl border border-gray-200 px-4 py-3 font-mono text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="Write your story in Markdown..."
        />
        <p className="mt-1 text-xs text-gray-500">
          Supports Markdown syntax. Paste raw text now; preview tools can be
          added later.
        </p>
      </div>

      {formError && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {formError}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate('/admin/blog')}
          disabled={formSubmitting}
          className="!rounded-lg"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={formSubmitting}
          className="!rounded-lg !bg-indigo-600 !text-white hover:!bg-indigo-500 focus:!ring-indigo-400"
        >
          {formSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-100 via-indigo-50 to-emerald-50">
      <div
        className="absolute inset-0 animate-[pulse_10s_ease-in-out_infinite] bg-gradient-to-br from-indigo-200/40 via-white/40 to-emerald-200/40"
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-5xl px-4 py-12">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-indigo-500">
              Blog Manager
            </p>
            <h1 className="gradient-text mt-2 text-4xl font-bold tracking-tight">
              Edit Blog Post
            </h1>
            <p className="mt-2 text-base text-gray-600">
              Update the story before sharing it with the community.
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/admin/blog')}
            className="!rounded-full"
          >
            Back to Posts
          </Button>
        </div>

        <div className="rounded-3xl bg-white/90 p-6 shadow-xl backdrop-blur">
          {loading ? (
            <div className="space-y-4">
              {[0, 1, 2].map((skeleton) => (
                <div
                  key={skeleton}
                  className="h-24 animate-pulse rounded-2xl bg-gray-100/70"
                />
              ))}
            </div>
          ) : loadError ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {loadError}
            </div>
          ) : (
            renderForm()
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminBlogEditPost;
